import type { Analytics } from "@models/analytics.model";
import { numberFormat } from "@/shared/utils/common";
import { groupConversionsByStep } from "@/shared/utils/conversionsByStep";

export default function ConversionsByTargetAction({ analytics }: { analytics: Analytics }) {
    const groups = groupConversionsByStep(analytics.charts?.["conversions-by-journey-step"] ?? [])
        .slice()
        .sort((a, b) => b.total - a.total);

    const total = groups.reduce((sum, g) => sum + g.total, 0);
    // Scale every bar to the largest drawn value: cell bars for dimensioned
    // steps, the step total for undimensioned single-bar steps.
    const max = groups.reduce((m, g) => {
        if (!g.dimensioned) return Math.max(m, g.total);
        return g.cells.reduce((cm, c) => Math.max(cm, c.conversions), m);
    }, 0);

    const barWidth = (value: number) => (max > 0 ? `${(value / max) * 100}%` : "0%");

    return (
        <div className="target-action-tile">
            <div className="target-action-tile__head">
                <div className="target-action-tile__title">Conversions by Target Action</div>
                {groups.length > 0 && (
                    <div className="target-action-tile__summary">
                        {numberFormat(total)} across {groups.length} action{groups.length === 1 ? "" : "s"}
                    </div>
                )}
            </div>
            {groups.length === 0 ? (
                <div className="target-action-tile__empty">No target actions configured.</div>
            ) : (
                <div className="target-action-tile__bars">
                    {groups.map((group) => {
                        // Undimensioned step: a single bar (subtotal == its one cell).
                        if (!group.dimensioned) {
                            return (
                                <div className="target-action-bar" key={group.journey_step_id ?? group.step_name}>
                                    <div className="target-action-bar__label" title={group.step_name}>{group.step_name}</div>
                                    <div className="target-action-bar__data">
                                        <div className="target-action-bar__track">
                                            <div className="target-action-bar__fill" style={{ width: barWidth(group.total) }} />
                                        </div>
                                        <div className="target-action-bar__value">{numberFormat(group.total)}</div>
                                    </div>
                                </div>
                            );
                        }

                        // Dimensioned step: subtotal header + a bar per dimension cell.
                        return (
                            <div className="target-action-group" key={group.journey_step_id ?? group.step_name}>
                                <div className="target-action-group__head d-flex justify-content-between fw-semibold" title={group.step_name}>
                                    <span className="text-truncate">{group.step_name}</span>
                                    <span>{numberFormat(group.total)}</span>
                                </div>
                                {group.cells.map((cell, idx) => (
                                    <div className="target-action-bar ps-3" key={cell.dimension_id ?? `cell-${idx}`}>
                                        <div className="target-action-bar__label" title={cell.dimension_name ?? "Unspecified"}>
                                            {cell.dimension_name ?? "Unspecified"}
                                        </div>
                                        <div className="target-action-bar__data">
                                            <div className="target-action-bar__track">
                                                <div className="target-action-bar__fill" style={{ width: barWidth(cell.conversions) }} />
                                            </div>
                                            <div className="target-action-bar__value">{numberFormat(cell.conversions)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
